import React, { useReducer } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getPath } from "common/routing";
import { UserAuthMethod } from "common-client/generated/graphql";
import {
  MFAReducer,
  SCREEN_DISPLAY,
  useMFAFlow,
} from "common-client/utils/useMFAFlow";
import {
  useAcceptInviteMutation,
  UserInviteQuery,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { colors, spacing } from "../../stitches.config";
import { validatePasswordWithTranslations } from "../../utils/passwords";
import { ProvidePhoneCode } from "../Authentication/ProvidePhoneCode";
import { ProvidePhoneNumber } from "../Authentication/ProvidePhoneNumber";
import { Button } from "../Common/Button";
import { Section, Sections } from "../Common/Layout";
import { TermsOfUseInput } from "../Common/TermsOfUseInput";
import { Body, Title } from "../Common/Typography";
import { Text } from "../Inputs/react-hook-form";
import { Container, Wrapper } from "./__styles__/UserInvite";
import { SSOProviderPicker } from "./SSOProviderPicker";

type UserInviteProps = {
  userInvite: NonNullable<UserInviteQuery["userInvite"]>;
};

interface FormStructure {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: Maybe<string>;
  password: Maybe<string>;
  terms: boolean;
  code?: Maybe<string>;
}

export const UserInvite = ({ userInvite }: UserInviteProps) => {
  const { addErrorToast } = useStatusToasts();
  const { t } = useTranslation();
  const [state, dispatch] = useMFAFlow({
    defaultValue: { currentScreen: SCREEN_DISPLAY.PROVIDE_CREDENTIALS },
    useReducer: useReducer as MFAReducer,
  });

  const onLoginSuccess = () => (window.location.href = getPath("map"));

  const handleSSORedirect = (accountId: string) => {
    const enabledProviders = userInvite.account.ssoProviders.filter(
      p => p.enabled,
    );

    switch (enabledProviders.length) {
      case 0:
        addErrorToast(
          "No SSO providers are configured for this account. Please contact your administrator.",
        );
        return;
      case 1:
        // @ts-ignore - were in the `length = 1` case, so this is safe
        window.location.href = `/auth/sso/initiate?accountId=${accountId}&providerId=${enabledProviders[0].id}`;
        return;
      default:
        dispatch({ type: "showSSOProviderPicker" });
    }
  };

  const [acceptInvite, { loading: mutationLoading }] = useAcceptInviteMutation({
    onCompleted: data => {
      if (!data.acceptInvite.ok) {
        dispatch({ type: "login", data: data.acceptInvite });
        return;
      }

      if (
        data.acceptInvite.requiresSSORedirect &&
        data.acceptInvite.accountId
      ) {
        handleSSORedirect(data.acceptInvite.accountId);
      } else {
        onLoginSuccess();
      }
    },
    onError: () => {
      addErrorToast(
        "There was an error accepting this invite. Please try again. If the problem persists, please email us at support@withforerunner.com",
      );
    },
  });

  const useFormProps = useForm<FormStructure>({
    defaultValues: {
      firstName: userInvite.firstName,
      lastName: userInvite.lastName,
      email: userInvite.email,
    },
  });
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useFormProps;

  const hasMultifactorAuthEnabled =
    userInvite.authMethod === UserAuthMethod.PASSWORD &&
    userInvite.account.securityConfiguration.multifactorAuthEnabled;

  return (
    <Wrapper>
      <Container>
        <FormProvider {...useFormProps}>
          <Sections as="form" gap={spacing.m}>
            {state.currentScreen === SCREEN_DISPLAY.PROVIDE_CREDENTIALS && (
              <>
                <Title size="large" type="semiBold">
                  {userInvite.authMethod === UserAuthMethod.SSO ?
                    "Complete your account setup"
                  : "Create your account"}
                </Title>
                <Section>
                  {userInvite.authMethod === UserAuthMethod.SSO ?
                    <Body size="default" type="regular" color="contentPrimary">
                      Your account uses Single Sign-On (SSO). Please verify your
                      information below, then you'll be redirected to sign in
                      through your organization's identity provider.
                    </Body>
                  : <Body
                      size="large"
                      type="emphasis"
                      color="contentPrimary"
                      style={{
                        backgroundColor: colors.bgUiContainer.value,
                        padding: spacing.m.value,
                      }}
                    >
                      {userInvite.email}
                    </Body>
                  }
                </Section>
                <Section flexDirection="row">
                  <Text
                    label="First Name"
                    error={errors.firstName?.message}
                    required
                    {...register("firstName", {
                      required: "Required field",
                    })}
                  />
                  <Text
                    label="Last Name"
                    error={errors.lastName?.message}
                    required
                    {...register("lastName", {
                      required: "Required field",
                    })}
                  />
                </Section>
                {userInvite.authMethod === UserAuthMethod.PASSWORD && (
                  <>
                    <Section>
                      <Text
                        type="tel"
                        label="Mobile phone number"
                        placeholder="(000) 000-0000"
                        error={errors.phoneNumber?.message}
                        {...register("phoneNumber", {
                          validate: {
                            required: value => {
                              if (!value && hasMultifactorAuthEnabled) {
                                return "Phone number is required";
                              }
                              return true;
                            },
                          },
                        })}
                        required={hasMultifactorAuthEnabled}
                      />
                    </Section>
                    {hasMultifactorAuthEnabled && (
                      <Body
                        size="small"
                        type="regular"
                        color="contentSecondary"
                      >
                        Note: Enter a phone number where we can send an
                        authenticationcode via SMS.
                      </Body>
                    )}
                  </>
                )}
                {userInvite.authMethod === UserAuthMethod.PASSWORD && (
                  <Section gap={spacing.s}>
                    <Text
                      label="Password"
                      type="password"
                      error={errors.password?.message}
                      placeholder="********"
                      required
                      {...register("password", {
                        validate: (val: Maybe<string>) => {
                          return validatePasswordWithTranslations({
                            password: val ?? "",
                            minimumPasswordLength:
                              userInvite.account.securityConfiguration
                                .minimumPasswordLength,
                            t,
                          });
                        },
                      })}
                    />
                    <Body size="small" type="regular" color="contentSecondary">
                      Create a password with at least{" "}
                      {
                        userInvite.account.securityConfiguration
                          .minimumPasswordLength
                      }{" "}
                      characters. Include at least one number, one upper case
                      letter, and one special character.
                    </Body>
                  </Section>
                )}
                <Section>
                  <TermsOfUseInput control={control} errors={errors} />
                  <Button
                    style={{
                      justifyContent: "center",
                      marginTop: spacing.m.value,
                    }}
                    type="submit"
                    styleVariant="primary"
                    size="medium"
                    onClick={handleSubmit(async data => {
                      await acceptInvite({
                        variables: {
                          firstName: data.firstName,
                          lastName: data.lastName,
                          inviteId: userInvite!.id,
                          // Only include password and phone for password auth
                          ...(userInvite.authMethod ===
                            UserAuthMethod.PASSWORD && {
                            password: data.password,
                            phoneNumber: data.phoneNumber,
                          }),
                        },
                      });
                    })}
                    disabled={mutationLoading}
                  >
                    {userInvite.authMethod === UserAuthMethod.SSO ?
                      "Continue to sign in"
                    : "Create account"}
                  </Button>
                </Section>
              </>
            )}

            {state.currentScreen === SCREEN_DISPLAY.SSO_PROVIDER_PICKER && (
              <SSOProviderPicker
                account={userInvite.account}
                providers={userInvite.account.ssoProviders.filter(
                  p => p.enabled,
                )}
                error={state.error}
              />
            )}

            {state.currentScreen === SCREEN_DISPLAY.PROVIDE_PHONE && (
              <ProvidePhoneNumber dispatch={dispatch} error={state.error} />
            )}
            {state.currentScreen === SCREEN_DISPLAY.PROVIDE_CODE && (
              <ProvidePhoneCode
                allowAnotherNumber={!state.phoneNumberVerified!}
                dispatch={dispatch}
                error={state.error ?? null}
                maskedPhoneNumber={state.phoneNumber!}
                onSuccess={onLoginSuccess}
              />
            )}
          </Sections>
        </FormProvider>
      </Container>
    </Wrapper>
  );
};
